import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/classnames.js';
import { shareGilding, type ShareOutcome } from '../lib/share.js';

/**
 * ShareGlyph — bespoke "send it out" mark, not a stock share node-graph.
 *
 * An open gilded tray with a leaf rising up and out of it: the body is drawn in
 * ink (currentColor) while the rising arrowhead is struck in gold (accent), so
 * the gesture reads as "a gilded page leaving your hands for the world" — the
 * same language as the app's "Send it out into the world" copy. Sized to the
 * surrounding type (1em-ish at 16px) so it sits cleanly in a row or a menu.
 */
export function ShareGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      {/* Open tray — ink hairline, open at the top where the page lifts out. */}
      <path
        d="M6.5 9.25H5.6A1.6 1.6 0 0 0 4 10.85v3.95A1.6 1.6 0 0 0 5.6 16.4h8.8a1.6 1.6 0 0 0 1.6-1.6v-3.95a1.6 1.6 0 0 0-1.6-1.6h-.9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Rising shaft. */}
      <path
        d="M10 12.4V4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* The leaf of gold lifting off — struck in accent so it's the focal mark. */}
      <path
        d="M6.9 6.7 10 3.5l3.1 3.2"
        stroke="rgb(var(--color-accent))"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M4 10.5 8 14.5 16 5.5"
        stroke="rgb(var(--color-accent))"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ShareButton — shares a gilding's public page. On a device with a native
 * share sheet it opens that; otherwise it copies the link and confirms inline
 * (glyph swaps to a gold check, label to "Link copied") for a beat.
 *
 * Two looks, one behaviour:
 *   - 'pill' — a bordered chip for action rows (the feed).
 *   - 'menu' — a full-width row for the gallery's card menu.
 */
export function ShareButton({
  id,
  variant = 'pill',
  className,
  onShared,
}: {
  id: string;
  variant?: 'pill' | 'menu';
  className?: string;
  onShared?: (outcome: ShareOutcome) => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const handle = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    const outcome = await shareGilding(id);
    onShared?.(outcome);
    if (outcome === 'copied') {
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    }
  };

  const label = copied ? 'Link copied' : 'Share';

  if (variant === 'menu') {
    return (
      <button
        type="button"
        role="menuitem"
        onClick={(e) => void handle(e)}
        aria-label="Share link"
        className={cn(
          'flex w-full items-center gap-2.5 rounded-xs px-3 py-2.5 text-left',
          'font-body text-micro text-ink transition-colors duration-150 hover:bg-surface-sunken',
          className,
        )}
      >
        {copied ? <CheckGlyph className="h-4 w-4 shrink-0" /> : <ShareGlyph className="h-4 w-4 shrink-0" />}
        <span>{copied ? 'Link copied' : 'Share link'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => void handle(e)}
      aria-label="Share this gilding"
      className={cn(
        'inline-flex select-none items-center gap-1.5 rounded-full border px-3 py-1.5',
        'border-hairline/70 text-ink-soft transition-colors duration-200',
        'hover:border-accent/40 hover:text-ink',
        className,
      )}
    >
      {copied ? <CheckGlyph className="shrink-0" /> : <ShareGlyph className="shrink-0" />}
      <span className="font-display text-micro leading-none">{label}</span>
    </button>
  );
}
